import { slugify } from '@/utility/helpers';
import { Context, Inject } from '@nuxt/types/app';

// Cache for lazy-loaded data sources
const dataSourceCache: Record<string, Record<string, any>> = {};
// Promise cache to prevent multiple simultaneous requests
const dataSourcePromises: Record<string, Promise<Record<string, any>> | undefined> = {};

export default async ({ app, route }: Context, inject: Inject) => {
  const langCode = route.path.split('/')[1] || 'de-de';
  const lang = langCode.split('-')[1];

  // Helper function to load data source lazily
  const loadDataSource = async (datasourceName: string) => {
    const cacheKey = `${datasourceName}-${lang}`;

    // Return cached data if available
    if (dataSourceCache[cacheKey]) {
      return dataSourceCache[cacheKey];
    }

    // Return existing promise if request is already in progress
    if (dataSourcePromises[cacheKey]) {
      return dataSourcePromises[cacheKey];
    }

    // Create and cache the promise
    dataSourcePromises[cacheKey] = (async () => {
      try {
        const dataSourceResponse = await app.$storyapi.get(`cdn/datasource_entries`, {
          resolve_links: 'url',
          datasource: datasourceName,
          dimension: lang,
          cv: Date.now(),
        });

        const dataSource: Record<string, any> = {};
        dataSourceResponse.data.datasource_entries.forEach((item: any) => {
          dataSource[slugify(item.name)] = {
            id: item.id,
            value: item.dimension_value || item.value,
          };
        });

        // Cache the result
        dataSourceCache[cacheKey] = dataSource;
        // Clean up promise cache
        delete dataSourcePromises[cacheKey];
        return dataSource;
      } catch (error) {
        console.error(`Error loading datasource ${datasourceName}:`, error);
        // Clean up promise cache on error
        delete dataSourcePromises[cacheKey];
        return {};
      }
    })();

    return dataSourcePromises[cacheKey];
  };

  // Inject lazy loader function
  inject('loadDataSource', loadDataSource);

  // Inject cache getter for already loaded data sources
  inject('getDataSourceCache', (datasourceName: string) => {
    const cacheKey = `${datasourceName}-${lang}`;
    return dataSourceCache[cacheKey] || {};
  });
};

declare module '@nuxt/types' {
  interface Context {
    $loadDataSource: (datasourceName: string) => Promise<Record<string, any>>;
    $getDataSourceCache: (datasourceName: string) => Record<string, any>;
  }
}

declare module 'vue/types/vue' {
  interface Vue {
    $loadDataSource: (datasourceName: string) => Promise<Record<string, any>>;
    $getDataSourceCache: (datasourceName: string) => Record<string, any>;
  }
}
